import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  ChevronRight, BookOpen, Zap, ArrowLeft, BarChart3,
  Clock, Award, Lightbulb, Info, ArrowRight, Minus
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function ExamReadiness({ onNavigate }) {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);

  const fetchReadiness = useCallback(async () => {
    try {
      const [readinessRes, historyRes] = await Promise.all([
        api.get('/learning/exam-readiness'),
        api.get('/readiness/history?days=14').catch(() => ({ data: [] }))
      ]);
      setReadiness(readinessRes.data);
      
      // Process history data for chart
      if (historyRes.data && historyRes.data.length > 0) {
        // Group by date and subject
        const grouped = {};
        historyRes.data.forEach(item => {
          if (!grouped[item.date]) {
            grouped[item.date] = { date: item.date };
          }
          grouped[item.date][item.subject] = item.score;
        });
        setHistoryData(Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date)));
      }
    } catch (err) {
      console.error('Failed to fetch readiness:', err);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'emerald';
    if (score >= 60) return 'amber';
    return 'red';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Critical';
  };

  const getTrendIcon = () => {
    if (readiness?.trend === 'improving') return <TrendingUp className="w-5 h-5 text-emerald-400" />;
    if (readiness?.trend === 'declining') return <TrendingDown className="w-5 h-5 text-red-400" />;
    return <Minus className="w-5 h-5 text-slate-400" />;
  };

  const getTrendColor = () => {
    if (readiness?.trend === 'improving') return 'text-emerald-400';
    if (readiness?.trend === 'declining') return 'text-red-400';
    return 'text-slate-400';
  };

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white font-['Outfit']">Exam Readiness</h1>
            <p className="text-xs text-slate-500">{user?.class_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 space-y-6">
        {/* Overall Score with Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/30 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-slate-400 text-sm">Overall Readiness</p>
                <button onClick={() => setShowTooltip(!showTooltip)} className="text-slate-500 hover:text-slate-300">
                  <Info className="w-4 h-4" />
                </button>
              </div>
              <p className="text-4xl font-bold text-white font-['Outfit']">
                {readiness?.confidence_range?.low || readiness?.overall_score || 0}–{readiness?.confidence_range?.high || readiness?.overall_score || 0}%
              </p>
              <div className={`flex items-center gap-1 mt-1 ${getTrendColor()}`}>
                {getTrendIcon()}
                <span className="text-sm font-medium">{readiness?.trend_label || 'No data yet'}</span>
              </div>
            </div>
            <div className={`w-20 h-20 rounded-full border-4 border-${getScoreColor(readiness?.overall_score || 0)}-500 flex items-center justify-center`}>
              <Target className={`w-10 h-10 text-${getScoreColor(readiness?.overall_score || 0)}-400`} />
            </div>
          </div>

          {/* Tooltip explaining the score */}
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-slate-900/80 rounded-lg p-3 mb-4"
            >
              <p className="text-xs text-slate-300 mb-2">This score is based on:</p>
              <ul className="text-xs text-slate-400 space-y-1">
                {(readiness?.inputs_used || ['quiz_scores', 'session_scores']).map((input, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    {input.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2">Recent sessions count 2× towards your score.</p>
            </motion.div>
          )}
          
          {/* Expected Score Range */}
          <div className="bg-slate-900/50 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-2">Expected Exam Score Range</p>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-cyan-400">
                {readiness?.confidence_range?.low || 0}% – {readiness?.confidence_range?.high || 0}%
              </span>
              {getTrendIcon()}
            </div>
          </div>
        </motion.div>

        {/* Readiness Trend Chart */}
        {historyData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
          >
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-400" />
              14-Day Trend
            </h3>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyData}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#64748b', fontSize: 10 }}
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fill: '#64748b', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  {readiness?.subjects?.slice(0, 4).map((subject, i) => (
                    <Line 
                      key={subject.name}
                      type="monotone" 
                      dataKey={subject.name}
                      stroke={['#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444'][i]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {/* Subject Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-lg font-semibold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-400" /> Subject Breakdown
          </h2>
          
          <div className="space-y-3">
            {(readiness?.subjects || []).map((subject, i) => {
              const color = getScoreColor(subject.score);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedSubject(selectedSubject === subject.name ? null : subject.name)}
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-violet-500/30 transition-all text-left"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{subjectIcons[subject.name] || '📚'}</span>
                      <div>
                        <p className="text-white font-medium">{subject.name}</p>
                        <p className={`text-xs text-${color}-400`}>{getScoreLabel(subject.score)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold text-${color}-400`}>{subject.score}%</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${subject.score}%` }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className={`h-full bg-${color}-500 rounded-full`}
                    />
                  </div>

                  {/* Expanded Topic Details */}
                  {selectedSubject === subject.name && subject.topics && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 pt-4 border-t border-slate-800 space-y-2"
                    >
                      {subject.topics.map((topic, j) => (
                        <div key={j} className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">{topic.name}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className={`h-full bg-${getScoreColor(topic.score)}-500 rounded-full`}
                                style={{ width: `${topic.score}%` }}
                              />
                            </div>
                            <span className={`text-${getScoreColor(topic.score)}-400 w-10 text-right`}>
                              {topic.score}%
                            </span>
                            {topic.score < 60 && (
                              <AlertTriangle className="w-4 h-4 text-amber-400" />
                            )}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Action Plan */}
        {readiness?.action_plan && readiness.action_plan.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-semibold text-white font-['Outfit'] mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" /> Action Plan
            </h2>
            
            <div className="space-y-3">
              {readiness.action_plan.map((action, i) => (
                <button
                  key={i}
                  onClick={() => onNavigate('daily-loop', { subject: action.subject, chapter: action.chapter })}
                  className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between ${
                    action.priority === 'high' 
                      ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50'
                      : 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${
                      action.priority === 'high' ? 'bg-red-500/20' : 'bg-amber-500/20'
                    } flex items-center justify-center`}>
                      {action.type === 'practice' ? (
                        <Zap className={`w-5 h-5 ${action.priority === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
                      ) : (
                        <BookOpen className={`w-5 h-5 ${action.priority === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{action.title}</p>
                      <p className="text-xs text-slate-400">{action.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{action.duration}</span>
                    <ChevronRight className="w-5 h-5" />
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Start Learning Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => onNavigate('daily-loop')}
            className="w-full bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white rounded-xl h-14 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-violet-500/30 transition-all"
          >
            <Zap className="w-5 h-5" /> Start 5-Minute Learning
          </button>
        </motion.div>

        {/* Study Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-900/30 border border-slate-800 rounded-xl p-4"
        >
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-violet-400" /> Tips to Improve
          </h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>Practice 5-10 minutes daily for consistent improvement</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>Focus on weak topics shown above</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>Review mistakes immediately after practice</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
